/**
 * 「빠른 추가」 바.
 *
 * 표의 빈 칸을 찾아 채우는 대신, 위에서 한 줄씩 담는다. 견적을 처음 쓰는 사람이
 * **어디에 뭘 넣어야 하는지** 고민하지 않아도 되고, 같은 손놀림이 반복된다.
 *
 * 작업내역을 치면 단가표가 지난 단가를 제안한다 — 고르면 단가까지 같이 들어온다.
 * 이것이 이 제품이 엑셀보다 나은 자리다.
 *
 * 엑셀 붙여넣기도 같은 바에 둔다. 이미 있는 표를 옮겨 적는 일이 새 견적의 절반인데,
 * 그 입구가 표 어딘가에 숨어 있으면 아무도 못 찾는다.
 */

import { useMemo, useState } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import { Textarea } from "@buildpy/ui/textarea";
import { lockedNow, openUnlessLocked, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";
import { PASTE_COLUMNS, parsePastedRows } from "@buildpy/core/modules/quote/editor/paste";
import Money from "@/components/money";
import { EditorTrade, FilledItemInput } from "@buildpy/core/modules/quote/editor/types";

/** 단가표가 돌려주는 제안. 화면이 고르면 단가까지 같이 들어온다. */
export interface QuickSuggestion {
  item: string;
  unit: string;
  material_unit_price: number;
  labor_unit_price: number;
  // 단가표 원가·적용 마진 — 원가 없는 줄은 null.
  cost_unit_price?: number | null;
  base_margin_bp?: number | null;
  used_count: number;
}

interface Props {
  trades: EditorTrade[];
  spaces: { code: string; label: string }[];
  /** 작업내역을 칠 때마다 부른다. 제안이 없으면 빈 배열. */
  onSearch: (tradeKey: string, keyword: string) => void;
  suggestions: QuickSuggestion[];
  onAdd: (tradeKey: string, item: FilledItemInput) => void;
  onPaste: (tradeKey: string, items: FilledItemInput[]) => void;
  /**
   * 간단 모드인가.
   *
   * 상세 모드에서까지 자재·노무를 한 칸으로 합치면 안 된다 — 발송할 때 그 값이
   * 단가표에 덮여, **다음 제안부터 노무가 0으로 굳는다.**
   */
  simple: boolean;
  // 잠긴 견적 — 칸·단추·공종 고르기를 끈다.
  disabled?: boolean;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 잠긴 직후·재렌더 전에 눌린 붙여넣기 「담기」 도 버린다.
  isLocked: () => boolean;
}

const emptyRow = (): Omit<FilledItemInput, "scope"> => ({
  space_code: "",
  item: "",
  unit: "",
  qty: 0,
  material_unit_price: 0,
  labor_unit_price: 0,
  note: "",
});

const QuickAdd = ({
  trades,
  spaces,
  onSearch,
  suggestions,
  onAdd,
  onPaste,
  simple,
  disabled = false,
  isLocked,
}: Props) => {
  const [tradeKey, setTradeKey] = useState("");
  const [row, setRow] = useState(emptyRow());
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");

  // 고른 공종이 지워졌으면 첫 공종으로 되돌린다 — 빈 값으로 담기를 누르면
  // 어디로 갔는지 알 수 없다.
  const activeKey = useMemo(() => {
    if (trades.some((t) => t.key === tradeKey)) return tradeKey;
    return trades[0]?.key ?? "";
  }, [trades, tradeKey]);

  const canAdd = activeKey !== "" && row.item.trim() !== "";

  /**
   * 간단 모드에서 칸에 보이는 값은 **자재 + 노무 합**이다.
   *
   * 자재만 보여 주면 상세에서 나눠 넣어 둔 값이 숨은 채로 담겨 「보이는 값과
   * 담기는 값이 다른」 상태가 된다 — 100,000 을 보고 담았는데 표에는 150,000 이
   * 들어간다.
   */
  const shownPrice = simple
    ? row.material_unit_price + row.labor_unit_price
    : row.material_unit_price;

  const add = () => {
    if (!canAdd) return;
    onAdd(activeKey, {
      ...row,
      scope: "",
      item: row.item.trim(),
      // 간단 모드는 값 하나짜리다 — 합쳐 넣고 노무를 비운다.
      material_unit_price: simple ? shownPrice : row.material_unit_price,
      labor_unit_price: simple ? 0 : row.labor_unit_price,
    });
    setRow(emptyRow());
  };

  // 잠긴 뒤 눌린 「담기」 는 버린다 — 창도 잠기면 닫힌다.
  const paste = unlessLocked(lockedNow(disabled, isLocked), () => {
    const { rows, skipped, unclear } = parsePastedRows(pasteText);
    if (rows.length === 0) {
      toast.error("읽을 수 있는 줄이 없습니다");
      return;
    }
    onPaste(activeKey, rows);
    setPasteOpen(false);
    setPasteText("");

    /**
     * 못 읽은 것은 **빠짐없이 말한다.**
     *
     * 「담았습니다」 만 띄우면 `32㎡`·`7,000원/㎡` 처럼 단위가 붙은 칸이 0 으로
     * 들어간 줄을 그대로 넘긴다. 견적서에 0원짜리 줄이 남고, 발송하고 나서야
     * 안다.
     */
    const notes = [
      skipped > 0 ? `${skipped}줄은 작업내역이 비어 건너뜀` : "",
      unclear > 0 ? `${unclear}줄은 수량·단가를 못 읽어 0 으로 담음` : "",
    ].filter(Boolean);

    const message =
      notes.length > 0
        ? `${rows.length}줄 담았습니다 — ${notes.join(" · ")}`
        : `${rows.length}줄 담았습니다`;

    if (unclear > 0) toast.warning(message);
    else toast.success(message);
  });

  return (
    <SectionCard className="border-accent/40 shadow-xs" padding="none">
      <div className="flex items-baseline justify-between gap-x-2 px-3 pt-2.5">
        <h2 className="text-sm font-semibold">빠른 추가</h2>
        <span className="hidden text-xs text-muted-foreground lg:inline">
          표를 찾아 채우는 대신 여기서 한 줄씩 넣습니다
        </span>
      </div>

      {/* 잠긴 안내는 감싸는 fieldset(데스크톱 본문·폰 빠른 추가 시트)이 가리킨다 — 가장 바깥에만 단다. */}
      <fieldset className="flex min-w-0 flex-wrap items-end gap-2 p-3 pt-2" disabled={disabled}>
        <Field label="공종">
          <SimpleSelect
            aria-label="담을 공종"
            className="w-32"
            disabled={disabled}
            options={trades.map((trade) => ({ value: trade.key, label: trade.name }))}
            size="sm"
            value={activeKey}
            onValueChange={setTradeKey}
          />
        </Field>

        <Field className="min-w-48 flex-1" label="작업내역 — 치면 지난 단가가 뜹니다">
          <Input
            aria-label="작업내역"
            inputSize="sm"
            disabled={disabled}
            name="quick-item"
            placeholder="실크벽지 도배"
            value={row.item}
            onChange={(e) => {
              // 글자를 바꾸면 고른 제안의 원가는 그 품목 것이 아니다.
              setRow((p) => ({ ...p, item: e.target.value, cost_unit_price: null, base_margin_bp: null }));
              onSearch(activeKey, e.target.value);
            }}
          />
        </Field>

        <Field label="공간">
          <SimpleSelect
            aria-label="공간"
            className="w-24"
            disabled={disabled}
            options={[{ value: "", label: "—" }, ...spaces.map((space) => ({ value: space.code, label: space.label }))]}
            size="sm"
            value={row.space_code}
            onValueChange={(next) => setRow((p) => ({ ...p, space_code: next }))}
          />
        </Field>

        <Field label="수량">
          <div className="w-20">
            <Input
              aria-label="수량"
              className="text-right tabular-nums"
              inputMode="decimal"
              inputSize="sm"
              disabled={disabled}
              name="quick-qty"
              value={row.qty === 0 ? "" : String(row.qty)}
              onChange={(e) =>
                setRow((p) => ({ ...p, qty: Number(e.target.value) || 0 }))
              }
            />
          </div>
        </Field>

        <Field label="규격">
          <div className="w-16">
            <Input
              aria-label="규격"
              inputSize="sm"
              disabled={disabled}
              name="quick-unit"
              value={row.unit}
              onChange={(e) => setRow((p) => ({ ...p, unit: e.target.value }))}
            />
          </div>
        </Field>

        <Field label={simple ? "단가" : "자재 단가"}>
          <div className="w-28">
            <Input
              aria-label={simple ? "단가" : "자재 단가"}
              className="text-right tabular-nums"
              inputMode="numeric"
              inputSize="sm"
              disabled={disabled}
              name="quick-price"
              value={shownPrice === 0 ? "" : String(shownPrice)}
              onChange={(e) =>
                setRow((p) => ({
                  ...p,
                  material_unit_price: Number(e.target.value) || 0,
                  // 간단 모드는 값 하나만 받는다 — 노무를 비워야 합계가 넣은 값과 같다.
                  labor_unit_price: simple ? 0 : p.labor_unit_price,
                }))
              }
            />
          </div>
        </Field>

        {!simple && (
          <Field label="노무 단가">
            <div className="w-28">
              <Input
                aria-label="노무 단가"
                className="text-right tabular-nums"
                inputMode="numeric"
                inputSize="sm"
                disabled={disabled}
                name="quick-labor"
                value={
                  row.labor_unit_price === 0 ? "" : String(row.labor_unit_price)
                }
                onChange={(e) =>
                  setRow((p) => ({
                    ...p,
                    labor_unit_price: Number(e.target.value) || 0,
                  }))
                }
              />
            </div>
          </Field>
        )}

        <Button disabled={disabled || !canAdd} size="sm" variant="outline" onClick={add}>
          추가
        </Button>

        <Button
          disabled={disabled || activeKey === ""}
          size="sm"
          variant="outline"
          onClick={() => setPasteOpen(true)}>
          엑셀 붙여넣기
        </Button>
      </fieldset>

      {suggestions.length > 0 && row.item.trim() !== "" && (
        <div className="border-t border-border bg-muted/30 pb-1.5">
          <p className="px-3 pb-1 pt-2 text-xs text-muted-foreground">
            지난 견적에서 쓴 단가 — 고르면 단가까지 같이 들어옵니다
          </p>
          {suggestions.map((s) => (
            <button
              key={`${s.item}\u0000${s.unit}`}
              className="flex w-full items-center justify-between gap-x-3 px-3 py-1.5 text-left hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              disabled={disabled}
              type="button"
              onClick={() =>
                setRow((p) => ({
                  ...p,
                  item: s.item,
                  unit: s.unit,
                  // 나뉜 값을 그대로 들여온다. 간단 모드는 칸에서 합으로
                  // 보이고, 담을 때 합쳐진다 — 어느 모드로 바꿔도 값이 안 샌다.
                  material_unit_price: s.material_unit_price,
                  labor_unit_price: s.labor_unit_price,
                  cost_unit_price: s.cost_unit_price ?? null,
                  base_margin_bp: s.base_margin_bp ?? null,
                }))
              }>
              <span className="flex min-w-0 items-center gap-x-2">
                <span className="truncate text-sm">{s.item}</span>
                <span className="rounded-md bg-background px-1 text-xs text-muted-foreground">
                  {s.unit || "—"}
                </span>
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  최근 {s.used_count}건
                </span>
              </span>
              <Money className="text-sm font-medium" value={s.material_unit_price + s.labor_unit_price} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={openUnlessLocked(pasteOpen, disabled)} onOpenChange={setPasteOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>엑셀에서 붙여넣기</DialogTitle>
          </DialogHeader>

          <DialogDescription>
            엑셀에서 표를 복사해 아래에 붙여 넣으세요. 열 순서는{" "}
            <b>{PASTE_COLUMNS.join(" · ")}</b> 입니다. 머리글 줄은 알아서
            건너뜁니다.
          </DialogDescription>

          <Textarea
            aria-label="붙여넣을 내용"
            className="min-h-40 font-mono text-xs"
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />

          <DialogFooter>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setPasteOpen(false)}>
              취소
            </Button>
            <Button
              disabled={pasteText.trim() === "" || disabled}
              size="sm"
              variant="primary"
              onClick={paste}>
              담기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};

export default QuickAdd;
